import React from 'react';
import { getImageUrl, getThemeColors } from './types';

interface ProfileCardHeaderProps {
  colorScheme: string;
  companyLogo: string | null;
  variant: 'preview' | 'full';
  /** Custom branding colors (takes precedence over colorScheme) */
  branding?: {
    primaryColor: string;
    secondaryColor: string;
  };
}

// CompanyProfileSettings defaults (see Mowazaf.Server/Data/PublicProfile/Entities/CompanyProfileSettings.cs).
// Treating a match against these as "HR has not customized" — so the employee's
// Color Scheme drives the bar. Any non-default branding value is treated as an
// explicit HR brand override and wins.
const BRANDING_DEFAULT_PRIMARY = '#3b82f6';
const BRANDING_DEFAULT_SECONDARY = '#6366f1';

const isHrBrandingCustomized = (b?: { primaryColor: string; secondaryColor: string }) =>
  !!b && (
    b.primaryColor?.toLowerCase() !== BRANDING_DEFAULT_PRIMARY ||
    b.secondaryColor?.toLowerCase() !== BRANDING_DEFAULT_SECONDARY
  );

const ProfileCardHeader: React.FC<ProfileCardHeaderProps> = ({
  colorScheme,
  companyLogo,
  variant,
  branding
}) => {
  const theme = getThemeColors(colorScheme);
  const heightClass = variant === 'preview' ? 'h-20' : 'h-32';
  // Fixed bounding box for the logo so any aspect ratio (square, banner,
  // portrait) renders without distortion via `object-contain`. The container
  // is transparent — no white card behind the logo — and instead leans on a
  // soft backdrop blur, a frosted scrim, and a drop shadow on the image
  // itself for legibility against the persona's colored header gradient.
  const logoBoxClass =
    variant === 'preview'
      ? 'h-7 w-16 p-0.5'
      : 'h-12 w-28 sm:w-32 p-1.5';
  const logoPositionClass =
    variant === 'preview'
      ? 'top-2 ltr:right-2 rtl:left-2'
      : 'top-4 ltr:right-4 rtl:left-4';

  // Employee Color Scheme drives the bar by default. HR branding only wins
  // when the admin has explicitly moved the primary/secondary colors away
  // from the CompanyProfileSettings defaults in /hr-admin-public-profile-settings.
  const useHrBranding = isHrBrandingCustomized(branding);
  const gradientStyle = useHrBranding
    ? { background: `linear-gradient(to right, ${branding!.primaryColor}, ${branding!.secondaryColor})` }
    : undefined;

  const gradientClass = useHrBranding ? '' : `bg-gradient-to-r ${theme.from} ${theme.to}`;

  return (
    <div
      className={`${heightClass} ${gradientClass} relative`}
      style={gradientStyle}
    >
      {companyLogo && (
        <div className={`absolute ${logoPositionClass}`}>
          {/* Transparent logo well: a soft backdrop-blur scrim + a drop-shadow
              on the image itself keeps the logo readable on any header color
              without slapping a hard white card behind it. */}
          <div
            className={`${logoBoxClass} bg-white/15 backdrop-blur-md rounded-md flex items-center justify-center ring-1 ring-white/25 shadow-lg`}
          >
            <img
              src={getImageUrl(companyLogo) || ''}
              alt="Company"
              className="max-h-full max-w-full object-contain drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileCardHeader;
